import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  MenuItem,
  Grid,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { fetchMatches, fetchTournaments } from '../../services/api';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { canManageTournament } from '../../utils/tournamentPermissions';
import { useUserLookup } from '../../hooks/useUserLookup';

const MatchesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: allMatches, isLoading, error } = useQuery(['matches'], () => fetchMatches());
  const { data: tournaments } = useQuery(['tournaments'], fetchTournaments);

  // Filter matches based on user role
  const matches = useMemo(() => {
    if (!allMatches) return [];
    
    const userRole = user?.['custom:role'];
    const userId = user?.userId;
    const isAdmin = userRole === 'admin';
    const isTournamentManager = userRole === 'tournament_manager';

    if (isAdmin) {
      // Admins see all matches
      return allMatches;
    }

    if (isTournamentManager) {
      // Tournament managers see matches for tournaments they manage
      const managedTournamentIds = (tournaments || [])
        .filter(t => canManageTournament(user, t))
        .map(t => t.id);
      
      return allMatches.filter(m => managedTournamentIds.includes(m.tournament_id));
    }

    // Regular users see only their own matches
    return allMatches.filter(m => 
      m.player1_id === userId || 
      m.player2_id === userId ||
      m.player1_team_id === userId ||
      m.player2_team_id === userId
    );
  }, [allMatches, user, tournaments]);
  
  // Get all unique player IDs from matches for user lookup
  const allPlayerIds = useMemo(() => {
    if (!matches) return [];
    const playerIds = new Set<string>();
    matches.forEach(match => {
      if (match.player1_id) playerIds.add(match.player1_id);
      if (match.player2_id) playerIds.add(match.player2_id);
      // Also include backend-populated player info
      if (match.player1?.id) playerIds.add(match.player1.id);
      if (match.player2?.id) playerIds.add(match.player2.id);
    });
    return Array.from(playerIds);
  }, [matches]);

  // Use user lookup hook for fallback
  const { getUserName } = useUserLookup(allPlayerIds);

  // Get all unique players from matches (now includes player names from backend)
  const allPlayers = useMemo(() => {
    if (!matches) return [];
    const playerMap = new Map<string, { id: string; name: string }>();
    matches.forEach(match => {
      if (match.player1) {
        playerMap.set(match.player1.id, match.player1);
      }
      if (match.player2) {
        playerMap.set(match.player2.id, match.player2);
      }
    });
    return Array.from(playerMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [matches]);

  // Helper function to get player name with fallback
  const getPlayerName = (_match: any, playerId?: string, playerInfo?: any) => {
    if (playerInfo?.name) return playerInfo.name;
    if (playerId) return getUserName(playerId);
    return 'TBD';
  };

  const [tournamentFilter, setTournamentFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [playerFilter, setPlayerFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'warning';
      case 'scheduled':
        return 'info';
      default:
        return 'default';
    }
  };

  // Filter matches based on selected filters
  const filteredMatches = useMemo(() => {
    if (!matches) return [];

    return matches.filter((match) => {
      // Tournament filter
      if (tournamentFilter !== 'all' && match.tournament_id !== tournamentFilter) {
        return false;
      }

      // Status filter
      if (statusFilter !== 'all' && match.status !== statusFilter) {
        return false;
      }

      // Player filter
      if (playerFilter !== 'all') {
        const isPlayer = match.player1_id === playerFilter || 
                        match.player2_id === playerFilter ||
                        match.player1_team_id === playerFilter ||
                        match.player2_team_id === playerFilter;
        if (!isPlayer) return false;
      }

      // Search query (tournament name, court, match number)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const tournamentName = (match.tournament_name || '').toLowerCase();
        const court = (match.court || '').toLowerCase();
        const matchNumber = match.match_number.toString();
        
        if (!tournamentName.includes(query) && 
            !court.includes(query) && 
            !matchNumber.includes(query)) {
          return false;
        }
      }

      return true;
    });
  }, [matches, tournamentFilter, statusFilter, playerFilter, searchQuery]);

  // Get unique statuses from matches
  const availableStatuses = useMemo(() => {
    if (!matches) return [];
    const statuses = new Set(matches.map(m => m.status));
    return Array.from(statuses);
  }, [matches]);

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error loading matches</div>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Matches</Typography>
        <Typography variant="body2" color="text.secondary">
          {filteredMatches.length} of {matches?.length || 0} matches
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Search"
              placeholder="Tournament, court, match #"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Tournament</InputLabel>
              <Select
                value={tournamentFilter}
                label="Tournament"
                onChange={(e) => setTournamentFilter(e.target.value)}
              >
                <MenuItem value="all">All Tournaments</MenuItem>
                {tournaments?.map((tournament) => (
                  <MenuItem key={tournament.id} value={tournament.id}>
                    {tournament.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                {availableStatuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status.replace('_', ' ').toUpperCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Player</InputLabel>
              <Select
                value={playerFilter}
                label="Player"
                onChange={(e) => setPlayerFilter(e.target.value)}
              >
                <MenuItem value="all">All Players</MenuItem>
                {allPlayers.map((player) => (
                  <MenuItem key={player.id} value={player.id}>
                    {player.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {filteredMatches.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tournament</TableCell>
                <TableCell>Players</TableCell>
                <TableCell>Round</TableCell>
                <TableCell>Match #</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Scheduled Time</TableCell>
                <TableCell>Court</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMatches.map((match) => {
                // Get player names with fallback
                const player1Name = getPlayerName(match, match.player1_id, match.player1);
                const player2Name = getPlayerName(match, match.player2_id, match.player2);
                const playersDisplay = match.match_type === 'singles' 
                  ? `${player1Name} vs ${player2Name}`
                  : match.team1?.team_name && match.team2?.team_name
                    ? `${match.team1.team_name} vs ${match.team2.team_name}`
                    : 'TBD vs TBD';
                
                return (
                  <TableRow key={match.id} hover>
                    <TableCell>{match.tournament_name || match.tournament_id}</TableCell>
                    <TableCell sx={{ minWidth: 200 }}>
                      <Typography variant="body2" noWrap title={playersDisplay}>
                        {playersDisplay}
                      </Typography>
                    </TableCell>
                    <TableCell>{match.round}</TableCell>
                    <TableCell>{match.match_number}</TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{match.match_type}</TableCell>
                    <TableCell>
                      {match.scheduled_time
                        ? format(new Date(match.scheduled_time), 'MMM dd, yyyy h:mm a')
                        : 'TBD'}
                    </TableCell>
                    <TableCell>{match.court || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={match.status.replace('_', ' ')}
                        color={getStatusColor(match.status)}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => navigate(`/matches/${match.id}`)}>
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No matches scheduled
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Matches will appear here once tournaments are created
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MatchesPage;
